import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Eraser, RotateCcw, Check, Eye, EyeOff, Play, 
  Lock, Unlock, Star, PenTool
} from 'lucide-react';
import { toast } from 'sonner';

const WRITING_COURSES = ['hindi_devanagari', 'arabic_alphabet', 'korean_alphabet'];

const CANVAS_SIZE = 380;
const BRUSH_SIZE_GUIDED = 14;
const BRUSH_SIZE_FREE = 10;
const GUIDE_FONT_SIZE = 260;
const GUIDE_COLOR = 'rgba(245, 158, 11, 0.12)';
const INK_COLOR = '#f59e0b';
const INK_COLOR_FREE = '#38bdf8';

export const isWritingCourse = (courseId) => WRITING_COURSES.includes(courseId);

export const WritingCanvas = ({ letter, letterName, courseId, isRTL, onScoreSubmit }) => {
  const canvasRef = useRef(null);
  const guideCanvasRef = useRef(null);
  const referenceCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [strokes, setStrokes] = useState([]);
  const [currentStroke, setCurrentStroke] = useState([]);
  const [writingMode, setWritingMode] = useState('guided'); // 'guided' | 'freehand'
  const [freehandUnlocked, setFreehandUnlocked] = useState(false);
  const [showGuide, setShowGuide] = useState(true);
  const [score, setScore] = useState(null);
  const [bestScore, setBestScore] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animProgress, setAnimProgress] = useState(0);
  const lastPoint = useRef(null);
  const animFrameRef = useRef(null);

  const getFontForCourse = useCallback(() => {
    switch (courseId) {
      case 'arabic_alphabet': return `${GUIDE_FONT_SIZE}px serif`;
      case 'korean_alphabet': return `${GUIDE_FONT_SIZE}px sans-serif`;
      case 'hindi_devanagari': return `${GUIDE_FONT_SIZE}px serif`;
      default: return `${GUIDE_FONT_SIZE}px serif`;
    }
  }, [courseId]);

  // Render the reference letter on hidden canvas (for accuracy scoring)
  const renderReference = useCallback(() => {
    const canvas = referenceCanvasRef.current;
    if (!canvas || !letter) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    ctx.fillStyle = '#000000';
    ctx.font = getFontForCourse();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    if (isRTL) ctx.direction = 'rtl';
    ctx.fillText(letter, CANVAS_SIZE / 2, CANVAS_SIZE / 2);
  }, [letter, getFontForCourse, isRTL]);

  // Render the faint guide letter
  const renderGuide = useCallback(() => {
    const canvas = guideCanvasRef.current;
    if (!canvas || !letter) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    
    if (writingMode === 'guided' && showGuide) {
      ctx.fillStyle = GUIDE_COLOR;
      ctx.font = getFontForCourse();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      if (isRTL) ctx.direction = 'rtl';
      ctx.fillText(letter, CANVAS_SIZE / 2, CANVAS_SIZE / 2);

      // Draw dotted outline for guidance
      ctx.strokeStyle = 'rgba(245, 158, 11, 0.2)';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 6]);
      ctx.font = getFontForCourse();
      ctx.strokeText(letter, CANVAS_SIZE / 2, CANVAS_SIZE / 2);
      ctx.setLineDash([]);
    }
  }, [letter, writingMode, showGuide, getFontForCourse, isRTL]);

  useEffect(() => { renderReference(); }, [renderReference]);
  useEffect(() => { renderGuide(); }, [renderGuide]);

  // Redraw all strokes on the main canvas
  const redrawStrokes = useCallback((strokesList = strokes) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    const color = writingMode === 'guided' ? INK_COLOR : INK_COLOR_FREE;
    const brushSize = writingMode === 'guided' ? BRUSH_SIZE_GUIDED : BRUSH_SIZE_FREE;

    strokesList.forEach(stroke => {
      if (stroke.length < 2) return;
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = brushSize;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.globalAlpha = 0.85;

      ctx.moveTo(stroke[0].x, stroke[0].y);
      for (let i = 1; i < stroke.length; i++) {
        const midX = (stroke[i - 1].x + stroke[i].x) / 2;
        const midY = (stroke[i - 1].y + stroke[i].y) / 2;
        ctx.quadraticCurveTo(stroke[i - 1].x, stroke[i - 1].y, midX, midY);
      }
      ctx.stroke();
      ctx.globalAlpha = 1;
    });
  }, [strokes, writingMode]);

  useEffect(() => { redrawStrokes(); }, [redrawStrokes]);

  // Reset on letter change
  useEffect(() => {
    clearCanvas();
    setScore(null);
    setBestScore(0);
    setFreehandUnlocked(false);
    setWritingMode('guided');
  }, [letter]);

  const getCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = CANVAS_SIZE / rect.width;
    const scaleY = CANVAS_SIZE / rect.height;

    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    if (isAnimating) return;
    setIsDrawing(true);
    setScore(null);
    const coords = getCoords(e);
    setCurrentStroke([coords]);
    lastPoint.current = coords;
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing || isAnimating) return;
    const coords = getCoords(e);

    // Smooth drawing: interpolate if distance is large
    const dx = coords.x - lastPoint.current.x;
    const dy = coords.y - lastPoint.current.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < 2) return;

    setCurrentStroke(prev => {
      const updated = [...prev, coords];

      // Live draw the current stroke
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const color = writingMode === 'guided' ? INK_COLOR : INK_COLOR_FREE;
      const brushSize = writingMode === 'guided' ? BRUSH_SIZE_GUIDED : BRUSH_SIZE_FREE;

      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = brushSize;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.globalAlpha = 0.85;

      if (updated.length >= 2) {
        const p1 = updated[updated.length - 2];
        const p2 = updated[updated.length - 1];
        const midX = (p1.x + p2.x) / 2;
        const midY = (p1.y + p2.y) / 2;
        ctx.moveTo(p1.x, p1.y);
        ctx.quadraticCurveTo(p1.x, p1.y, midX, midY);
      }
      ctx.stroke();
      ctx.globalAlpha = 1;

      return updated;
    });

    lastPoint.current = coords;
  };

  const endDrawing = (e) => {
    if (e) e.preventDefault();
    if (!isDrawing) return;
    setIsDrawing(false);
    if (currentStroke.length > 1) {
      setStrokes(prev => [...prev, currentStroke]);
    }
    setCurrentStroke([]);
    lastPoint.current = null;
  };

  const clearCanvas = () => {
    setStrokes([]);
    setCurrentStroke([]);
    setScore(null);
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    }
  };

  const undoStroke = () => {
    if (strokes.length === 0) return;
    setStrokes(prev => {
      const updated = prev.slice(0, -1);
      // Force redraw
      setTimeout(() => redrawStrokes(updated), 0);
      return updated;
    });
    setScore(null);
  };

  // Accuracy scoring: compare user drawing with reference
  const calculateAccuracy = () => {
    const refCanvas = referenceCanvasRef.current;
    const drawCanvas = canvasRef.current;
    if (!refCanvas || !drawCanvas) return 0;

    const refCtx = refCanvas.getContext('2d');
    const drawCtx = drawCanvas.getContext('2d');

    const refData = refCtx.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE).data;
    const drawData = drawCtx.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE).data;

    let refPixels = 0;
    let drawPixels = 0;
    let overlap = 0;

    // Use an expanded reference (dilate by brush radius for tolerance)
    const tolerance = writingMode === 'guided' ? BRUSH_SIZE_GUIDED : BRUSH_SIZE_FREE + 4;
    const refSet = new Set();

    for (let i = 0; i < refData.length; i += 4) {
      if (refData[i + 3] > 50) {
        refPixels++;
        const px = (i / 4) % CANVAS_SIZE;
        const py = Math.floor((i / 4) / CANVAS_SIZE);
        // Add surrounding pixels to tolerance set
        for (let dx = -tolerance; dx <= tolerance; dx += 2) {
          for (let dy = -tolerance; dy <= tolerance; dy += 2) {
            const nx = px + dx;
            const ny = py + dy;
            if (nx >= 0 && nx < CANVAS_SIZE && ny >= 0 && ny < CANVAS_SIZE) {
              refSet.add(ny * CANVAS_SIZE + nx);
            }
          }
        }
      }
    }

    for (let i = 0; i < drawData.length; i += 4) {
      if (drawData[i + 3] > 50) {
        drawPixels++;
        const px = (i / 4) % CANVAS_SIZE;
        const py = Math.floor((i / 4) / CANVAS_SIZE);
        if (refSet.has(py * CANVAS_SIZE + px)) {
          overlap++;
        }
      }
    }

    if (drawPixels === 0 || refPixels === 0) return 0;

    const coverage = overlap / drawPixels; // precision: how much of your drawing is on the letter
    const recall = Math.min(overlap / (refPixels * 0.3), 1); // recall: how much of the letter you covered (reduced target)
    
    if (coverage + recall === 0) return 0;
    const f1 = 2 * (coverage * recall) / (coverage + recall);
    return Math.round(Math.min(f1 * 100, 100));
  };

  const checkAccuracy = () => {
    if (strokes.length === 0) {
      toast.error('Draw the letter first!');
      return;
    }
    const acc = calculateAccuracy();
    setScore(acc);

    if (acc > bestScore) {
      setBestScore(acc);
    }

    if (acc >= 70 && writingMode === 'guided' && !freehandUnlocked) {
      setFreehandUnlocked(true);
      toast.success('Freehand mode unlocked! Score: ' + acc + '%');
    } else if (acc >= 85) {
      toast.success('Excellent! ' + acc + '%');
    } else if (acc >= 60) {
      toast('Good effort! ' + acc + '%. Keep practicing.');
    } else {
      toast('Try to trace closer to the letter outline. ' + acc + '%');
    }

    if (onScoreSubmit) {
      onScoreSubmit({ mode: writingMode, score: acc, letter, letterName });
    }
  };

  // Stroke order animation
  const playStrokeAnimation = useCallback(() => {
    if (isAnimating) return;
    clearCanvas();
    setIsAnimating(true);
    setAnimProgress(0);

    const refCanvas = referenceCanvasRef.current;
    if (!refCanvas) return;
    const refCtx = refCanvas.getContext('2d');
    const refData = refCtx.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE).data;

    // Find the bounding box of the letter
    let minX = CANVAS_SIZE, maxX = 0, minY = CANVAS_SIZE, maxY = 0;
    for (let i = 0; i < refData.length; i += 4) {
      if (refData[i + 3] > 50) {
        const px = (i / 4) % CANVAS_SIZE;
        const py = Math.floor((i / 4) / CANVAS_SIZE);
        minX = Math.min(minX, px);
        maxX = Math.max(maxX, px);
        minY = Math.min(minY, py);
        maxY = Math.max(maxY, py);
      }
    }

    const width = maxX - minX;
    const duration = 2000;
    const startTime = performance.now();
    const direction = isRTL ? -1 : 1;

    const animate = (time) => {
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / duration, 1);
      setAnimProgress(progress);

      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

      // Progressive reveal with gradient edge
      const revealX = direction === 1
        ? minX + width * progress
        : maxX - width * progress;

      const imgData = ctx.createImageData(CANVAS_SIZE, CANVAS_SIZE);
      
      for (let i = 0; i < refData.length; i += 4) {
        if (refData[i + 3] > 50) {
          const px = (i / 4) % CANVAS_SIZE;
          const py = Math.floor((i / 4) / CANVAS_SIZE);
          
          const revealed = direction === 1 ? px <= revealX : px >= revealX;
          
          if (revealed) {
            // Distance from reveal edge for glow effect
            const edgeDist = direction === 1 
              ? revealX - px 
              : px - revealX;
            
            const glowAlpha = edgeDist < 15 ? 0.5 + 0.5 * (edgeDist / 15) : 1;
            
            // Amber color with glow
            if (edgeDist < 8) {
              imgData.data[i] = 255;     // R
              imgData.data[i + 1] = 200; // G
              imgData.data[i + 2] = 50;  // B
              imgData.data[i + 3] = Math.round(255 * glowAlpha);
            } else {
              imgData.data[i] = 245;     // R
              imgData.data[i + 1] = 158; // G
              imgData.data[i + 2] = 11;  // B
              imgData.data[i + 3] = Math.round(220 * glowAlpha);
            }
          }
        }
      }
      
      ctx.putImageData(imgData, 0, 0);

      if (progress < 1) {
        animFrameRef.current = requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
        setTimeout(() => {
          ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
        }, 1500);
      }
    };

    animFrameRef.current = requestAnimationFrame(animate);
  }, [isAnimating, isRTL, letter]);

  useEffect(() => {
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  const switchMode = (newMode) => {
    if (newMode === 'freehand' && !freehandUnlocked) {
      toast.error('Score 70%+ in guided mode to unlock freehand!');
      return;
    }
    setWritingMode(newMode);
    clearCanvas();
    setScore(null);
  };

  const getScoreStars = (s) => {
    if (s >= 90) return 3;
    if (s >= 70) return 2;
    if (s >= 50) return 1;
    return 0;
  };

  return (
    <div className="flex flex-col items-center gap-4" data-testid="writing-canvas-container">
      {/* Mode Tabs */}
      <div className="flex items-center gap-2 bg-slate-800 rounded-lg p-1">
        <button
          onClick={() => switchMode('guided')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            writingMode === 'guided'
              ? 'bg-amber-500 text-slate-900'
              : 'text-slate-400 hover:text-white'
          }`}
          data-testid="writing-mode-guided"
        >
          <Eye className="w-4 h-4" />
          Guided Tracing
        </button>
        <button
          onClick={() => switchMode('freehand')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            writingMode === 'freehand'
              ? 'bg-sky-500 text-slate-900'
              : freehandUnlocked
                ? 'text-slate-400 hover:text-white'
                : 'text-slate-600 cursor-not-allowed'
          }`}
          data-testid="writing-mode-freehand"
        >
          {freehandUnlocked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
          Freehand
        </button>
      </div>

      {/* Info text */}
      <p className="text-sm text-slate-500 text-center">
        {writingMode === 'guided' 
          ? 'Trace over the faint letter. Score 70%+ to unlock freehand mode.'
          : 'Write the letter from memory. No guide shown.'}
      </p>

      {/* Canvas Area */}
      <div className="relative" style={{ width: CANVAS_SIZE, maxWidth: '100%' }}>
        <div 
          className={`relative rounded-2xl border-2 overflow-hidden ${
            writingMode === 'guided' 
              ? 'border-amber-500/30 bg-slate-900/80' 
              : 'border-sky-500/30 bg-slate-900/80'
          }`}
          style={{ 
            width: '100%', 
            paddingBottom: '100%',
            position: 'relative'
          }}
        >
          {/* Guide canvas (faint letter) */}
          <canvas
            ref={guideCanvasRef}
            width={CANVAS_SIZE}
            height={CANVAS_SIZE}
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ zIndex: 1 }}
          />
          
          {/* Drawing canvas */}
          <canvas
            ref={canvasRef}
            width={CANVAS_SIZE}
            height={CANVAS_SIZE}
            className="absolute inset-0 w-full h-full touch-none"
            style={{ zIndex: 2, cursor: isAnimating ? 'default' : 'crosshair' }}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={endDrawing}
            onMouseLeave={endDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={endDrawing}
            data-testid="writing-canvas"
          />

          {/* Hidden reference canvas for scoring */}
          <canvas
            ref={referenceCanvasRef}
            width={CANVAS_SIZE}
            height={CANVAS_SIZE}
            className="hidden"
          />

          {/* Center cross guides */}
          <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }}>
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-slate-700/30" />
            <div className="absolute top-1/2 left-0 right-0 h-px bg-slate-700/30" />
          </div>

          {/* Animation progress bar */}
          {isAnimating && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-800" style={{ zIndex: 3 }}>
              <div 
                className="h-full bg-amber-500 transition-all"
                style={{ width: `${animProgress * 100}%` }}
              />
            </div>
          )}
        </div>

        {/* Score overlay */}
        {score !== null && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 4 }}>
            <div className={`px-6 py-3 rounded-2xl backdrop-blur-md border ${
              score >= 70 
                ? 'bg-green-500/20 border-green-500/40' 
                : 'bg-orange-500/20 border-orange-500/40'
            }`}>
              <div className="flex items-center gap-3">
                <span className={`text-3xl font-bold ${score >= 70 ? 'text-green-400' : 'text-orange-400'}`}>
                  {score}%
                </span>
                <div className="flex gap-0.5">
                  {[1, 2, 3].map(i => (
                    <Star 
                      key={i} 
                      className={`w-5 h-5 ${i <= getScoreStars(score) ? 'text-amber-400 fill-amber-400' : 'text-slate-600'}`} 
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center gap-2 flex-wrap justify-center">
        <button
          onClick={playStrokeAnimation}
          disabled={isAnimating}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm transition-colors disabled:opacity-50"
          data-testid="stroke-animation-btn"
          title="Watch stroke order"
        >
          <Play className="w-4 h-4 text-amber-400" />
          Stroke Order
        </button>

        {writingMode === 'guided' && (
          <button
            onClick={() => { setShowGuide(!showGuide); renderGuide(); }}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm transition-colors"
            data-testid="toggle-guide-btn"
          >
            {showGuide ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {showGuide ? 'Hide' : 'Show'} Guide
          </button>
        )}

        <button
          onClick={undoStroke}
          disabled={strokes.length === 0 || isAnimating}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm transition-colors disabled:opacity-50"
          data-testid="undo-stroke-btn"
        >
          <RotateCcw className="w-4 h-4" />
          Undo
        </button>

        <button
          onClick={clearCanvas}
          disabled={strokes.length === 0 || isAnimating}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm transition-colors disabled:opacity-50"
          data-testid="clear-canvas-btn"
        >
          <Eraser className="w-4 h-4" />
          Clear
        </button>

        <button
          onClick={checkAccuracy}
          disabled={strokes.length === 0 || isAnimating}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-colors disabled:opacity-50 ${
            writingMode === 'guided'
              ? 'bg-amber-500 hover:bg-amber-400 text-slate-900'
              : 'bg-sky-500 hover:bg-sky-400 text-slate-900'
          }`}
          data-testid="check-accuracy-btn"
        >
          <Check className="w-4 h-4" />
          Check
        </button>
      </div>

      {/* Best score */}
      {bestScore > 0 && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Star className="w-4 h-4 text-amber-500" />
          Best: {bestScore}%
          <span className="text-slate-600">|</span>
          <div className="flex gap-0.5">
            {[1, 2, 3].map(i => (
              <Star 
                key={i}
                className={`w-3.5 h-3.5 ${i <= getScoreStars(bestScore) ? 'text-amber-400 fill-amber-400' : 'text-slate-700'}`} 
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WritingCanvas;
